import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import StickyActionHeader from '../../components/StickyActionHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Plus, 
  Circle,
  CheckCircle,
  Clock,
  X,
  Truck,
  Key,
  MapPin,
  Phone,
  CaretDown,
  CaretRight,
  Camera,
  PaintBrush,
  ForkKnife,
  MusicNotes,
  CurrencyInr,
  ClipboardText,
  Users
} from '@phosphor-icons/react';
import Bed from '../../components/icons/RoomBedIcon';
import api from '../../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import EventAwareDatePicker from '../../components/EventAwareDatePicker';

const statusConfig = {
  todo: { label: 'To Do', color: 'bg-gray-100 text-gray-700', icon: Circle },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

const taskStatusOrder = ['todo', 'in_progress', 'completed'];

const taskCategories = [
  { value: 'planning', label: 'Planning', icon: ClipboardText },
  { value: 'transport', label: 'Transport', icon: Truck },
  { value: 'hotel', label: 'Hotel', icon: Bed },
  { value: 'vendor', label: 'Vendor', icon: Users },
  { value: 'decoration', label: 'Decoration', icon: PaintBrush },
  { value: 'finance', label: 'Finance', icon: CurrencyInr },
  { value: 'photography', label: 'Photography', icon: Camera },
  { value: 'makeup', label: 'Makeup', icon: Users },
  { value: 'catering', label: 'Catering', icon: ForkKnife },
  { value: 'entertainment', label: 'Entertainment', icon: MusicNotes },
];

const priorityLabels = {
  1: 'Urgent',
  2: 'High',
  3: 'Medium',
  4: 'Low',
  5: 'Nice'
};

const journeyStatusLabels = {
  waiting_for_pickup: 'Waiting for Pickup',
  pickup_assigned: 'Pickup Assigned',
  picked_up: 'Picked Up',
  reached_hotel: 'Reached Hotel',
  waiting_for_room: 'Waiting for Room',
  room_ready: 'Room Ready',
  keys_handed_over: 'Keys Handed Over',
  checked_in: 'Checked In'
};

const journeyActions = {
  transport: [
    { value: 'start_pickup', label: 'Start Pickup', icon: Truck },
    { value: 'picked_up', label: 'Mark Guest Picked Up', icon: Truck },
    { value: 'reached_hotel', label: 'Mark Guest Reached Hotel', icon: MapPin },
  ],
  hotel: [
    { value: 'assign_room', label: 'Assign Room Needed', icon: Bed },
    { value: 'room_ready', label: 'Mark Room Ready', icon: Bed },
    { value: 'keys_handed_over', label: 'Hand Over Keys', icon: Key },
    { value: 'checked_in', label: 'Mark Checked In', icon: CheckCircle },
  ],
};

export default function TaskBoard() {
  const { currentEvent, isTeamMember } = useAuth();
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(searchParams.get('action') === 'add');
  const [expandedTaskIds, setExpandedTaskIds] = useState([]);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [mobileStatus, setMobileStatus] = useState('todo');
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 2,
    assigned_to_user_id: '',
    category: 'planning',
    related_guest_id: '',
    journey_action: ''
  });

  useEffect(() => {
    fetchTasks();
    if (!isTeamMember) {
      fetchTeamMembers();
      fetchGuests();
    }
  }, [currentEvent?.id, isTeamMember]);

  const fetchTasks = async () => {
    if (!currentEvent?.id) return;
    try {
      const response = await api.get(`/events/${currentEvent.id}/tasks`);
      setTasks((response.data || []).filter((task) => !task.financial_record_id));
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchGuests = async () => {
    if (!currentEvent?.id) return;
    try {
      const response = await api.get(`/events/${currentEvent.id}/guests`);
      setGuests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch guests:', error);
    }
  };

  const fetchTeamMembers = async () => {
    if (!currentEvent?.id) return;
    try {
      const response = await api.get(`/events/${currentEvent.id}/team-members`);
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${currentEvent.id}/tasks`, {
        title: newTask.title,
        description: newTask.description,
        priority: newTask.priority,
        assigned_to_user_id: activeTeamMembers.length > 0
          ? newTask.assigned_to_user_id || null
          : null,
        category: newTask.category,
        related_guest_id: newTask.related_guest_id || null,
        journey_action: newTask.journey_action || null,
        due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null
      });
      toast.success('Task added successfully');
      setShowAddDialog(false);
      setNewTask({ title: '', description: '', due_date: '', priority: 2, assigned_to_user_id: '', category: 'planning', related_guest_id: '', journey_action: '' });
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add task');
    }
  };

  const handleJourneyAction = async (task, action) => {
    try {
      await api.put(`/events/${currentEvent.id}/tasks/${task.id}`, { journey_action: action });
      toast.success(task.category === 'transport' ? 'Transport workflow updated' : 'Guest journey updated');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update guest journey');
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/events/${currentEvent.id}/tasks/${taskId}`, { status: newStatus });
      toast.success('Task updated');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    try {
      await api.delete(`/events/${currentEvent.id}/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const toggleTaskExpanded = (taskId) => {
    setExpandedTaskIds((current) => {
      if (current.includes(taskId)) {
        return current.filter((id) => id !== taskId);
      }
      return [taskId, ...current].slice(0, 2);
    });
  };

  const toggleCategoryCollapsed = (status, category) => {
    const key = `${status}:${category}`;
    setCollapsedCategories((current) => ({
      ...current,
      [key]: !(current[key] ?? true)
    }));
  };

  const getTasksByStatus = (status) => tasks.filter(t => t.status === status);
  const activeTeamMembers = teamMembers.filter((member) => member.is_active !== false);
  const categoryLabel = (value) => taskCategories.find((item) => item.value === value)?.label || 'Planning';
  const getGuestName = (task) => task.related_guest?.name || guests.find((guest) => guest.id === task.related_guest_id)?.name;
  const priorityBadgeClass = (priority) => (
    ['bg-red-100 text-red-700', 'bg-orange-100 text-orange-700', 'bg-yellow-100 text-yellow-700', 'bg-green-100 text-green-700', 'bg-blue-100 text-blue-700'][Math.max(1, Math.min(priority || 3, 5)) - 1]
  );
  const formatDueSummary = (dueDate) => {
    if (!dueDate) return 'No due date';
    const due = new Date(dueDate);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const dueDay = format(due, 'yyyy-MM-dd');
    if (dueDay === format(today, 'yyyy-MM-dd')) return `Today ${format(due, 'h:mm a')}`;
    if (dueDay === format(tomorrow, 'yyyy-MM-dd')) return `Tomorrow ${format(due, 'h:mm a')}`;
    return format(due, 'MMM d, h:mm a');
  };

  const getCategoryCounts = (categoryTasks) => ({
    pending: categoryTasks.filter((task) => task.status !== 'completed').length,
    completed: categoryTasks.filter((task) => task.status === 'completed').length
  });

  const getTaskDueTime = (task) => task.due_date ? new Date(task.due_date).getTime() : Number.MAX_SAFE_INTEGER;

  const getCategoryUrgency = (categoryTasks) => {
    const pendingTasks = categoryTasks.filter((task) => task.status !== 'completed');
    const now = new Date();
    const todayKey = format(now, 'yyyy-MM-dd');
    const soonLimit = now.getTime() + (48 * 60 * 60 * 1000);
    const dueTimes = pendingTasks.map(getTaskDueTime);

    const overdueCount = pendingTasks.filter((task) => {
      const dueTime = getTaskDueTime(task);
      return dueTime !== Number.MAX_SAFE_INTEGER && dueTime < now.getTime();
    }).length;

    return {
      pendingCount: pendingTasks.length,
      earliestDue: dueTimes.length ? Math.min(...dueTimes) : Number.MAX_SAFE_INTEGER,
      highestPriority: pendingTasks.length ? Math.min(...pendingTasks.map((task) => task.priority || 3)) : Number.MAX_SAFE_INTEGER,
      overdueCount,
      hasDueToday: pendingTasks.some((task) => task.due_date && format(new Date(task.due_date), 'yyyy-MM-dd') === todayKey),
      hasDueSoon: pendingTasks.some((task) => {
        const dueTime = getTaskDueTime(task);
        return dueTime !== Number.MAX_SAFE_INTEGER && dueTime >= now.getTime() && dueTime <= soonLimit;
      }),
      hasHighPriority: pendingTasks.some((task) => (task.priority || 3) <= 2)
    };
  };

  const getCategoryUrgencyIndicator = (urgency) => {
    if (urgency.overdueCount > 0) {
      return { label: 'Overdue', className: 'text-red-700' };
    }
    if (urgency.hasDueToday) {
      return { label: 'Due Today', className: 'text-orange-700' };
    }
    if (urgency.hasHighPriority) {
      return { label: 'Needs Attention', className: 'text-rose-700' };
    }
    return null;
  };

  const getTransportNextAction = (task) => {
    if (task.category !== 'transport' || task.status === 'completed' || !task.related_guest_id) {
      return null;
    }

    const journeyStatus = task.related_guest?.journey_status;
    if (task.status === 'in_progress' || journeyStatus === 'picked_up') {
      return { value: 'reached_hotel', label: 'Mark Guest Reached Hotel', icon: MapPin };
    }
    if (journeyStatus === 'pickup_assigned') {
      return { value: 'picked_up', label: 'Mark Guest Picked Up', icon: Truck };
    }
    return { value: 'start_pickup', label: 'Start Pickup', icon: Truck };
  };

  const parseNoteValue = (notes, labels) => {
    if (!notes) return null;
    const escapedLabels = labels.map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const match = notes.match(new RegExp(`(?:${escapedLabels})\\s*[:\\-]\\s*([^\\n,;]+)`, 'i'));
    return match?.[1]?.trim() || null;
  };

  const getDueDateGroup = (task) => {
    if (!task.due_date) return { key: 'no_due_date', label: 'No Due Date', order: Number.MAX_SAFE_INTEGER };
    const due = new Date(task.due_date);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const dueDay = format(due, 'yyyy-MM-dd');
    const todayDay = format(today, 'yyyy-MM-dd');
    const tomorrowDay = format(tomorrow, 'yyyy-MM-dd');
    if (dueDay === todayDay) return { key: dueDay, label: 'Today', order: due.getTime() };
    if (dueDay === tomorrowDay) return { key: dueDay, label: 'Tomorrow', order: due.getTime() };
    return { key: dueDay, label: format(due, 'MMM d, yyyy'), order: due.getTime() };
  };

  const compareTasks = (a, b) => {
    const dueA = a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    const dueB = b.due_date ? new Date(b.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    if (dueA !== dueB) return dueA - dueB;
    const priorityA = a.priority || 3;
    const priorityB = b.priority || 3;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
  };

  const groupTasksForColumn = (columnTasks) => {
    const knownCategoryValues = taskCategories.map((category) => category.value);
    const customCategories = Array.from(new Set(
      columnTasks
        .map((task) => task.category || 'planning')
        .filter((category) => !knownCategoryValues.includes(category))
    )).map((category) => ({
      value: category,
      label: categoryLabel(category),
      icon: ClipboardText
    }));

    return [...taskCategories, ...customCategories]
    .map((category) => {
      const categoryTasks = columnTasks
        .filter((task) => (task.category || 'planning') === category.value)
        .sort(compareTasks);
      const dueGroups = categoryTasks.reduce((groups, task) => {
        const dueGroup = getDueDateGroup(task);
        if (!groups[dueGroup.key]) {
          groups[dueGroup.key] = { ...dueGroup, tasks: [] };
        }
        groups[dueGroup.key].tasks.push(task);
        return groups;
      }, {});

      return {
        ...category,
        defaultOrder: knownCategoryValues.includes(category.value)
          ? knownCategoryValues.indexOf(category.value)
          : knownCategoryValues.length + customCategories.findIndex((item) => item.value === category.value),
        urgency: getCategoryUrgency(categoryTasks),
        tasks: categoryTasks,
        dueGroups: Object.values(dueGroups).sort((a, b) => a.order - b.order),
      };
    })
    .filter((category) => category.tasks.length > 0)
    .sort((a, b) => {
      const urgencyA = a.urgency;
      const urgencyB = b.urgency;

      if (urgencyA.earliestDue !== urgencyB.earliestDue) {
        return urgencyA.earliestDue - urgencyB.earliestDue;
      }
      if (urgencyA.highestPriority !== urgencyB.highestPriority) {
        return urgencyA.highestPriority - urgencyB.highestPriority;
      }
      if (urgencyA.overdueCount !== urgencyB.overdueCount) {
        return urgencyB.overdueCount - urgencyA.overdueCount;
      }
      return a.defaultOrder - b.defaultOrder;
    });
  };

  const VendorContext = ({ task }) => {
    const vendors = task.vendors || [];
    if (vendors.length === 0 && !task.related_guest) return null;

    return (
      <div className="mb-3 space-y-2 rounded-md border border-border bg-muted/30 p-3">
        {vendors.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Vendor</p>
            {vendors.map((vendor) => {
              const driverName = parseNoteValue(vendor.notes, ['driver', 'driver name']);
              const vehicle = parseNoteValue(vendor.notes, ['vehicle', 'vehicle no', 'vehicle number', 'car']);
              return (
                <div key={vendor.id} className="space-y-1 text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground">{vendor.name}</span>
                    <Badge variant="outline" className="text-[0.6875rem]">{vendor.category}</Badge>
                  </div>
                  {vendor.contact_name && <p className="text-muted-foreground">Contact: {vendor.contact_name}</p>}
                  {vendor.phone && (
                    <a href={`tel:${vendor.phone}`} className="inline-flex items-center gap-1 text-wedding-sage hover:underline">
                      <Phone className="h-3.5 w-3.5" /> {vendor.phone}
                    </a>
                  )}
                  {task.category === 'transport' && driverName && <p className="text-muted-foreground">Driver: {driverName}</p>}
                  {task.category === 'transport' && vehicle && <p className="text-muted-foreground">Vehicle: {vehicle}</p>}
                  {vendor.notes && <p className="line-clamp-2 text-muted-foreground">Notes: {vendor.notes}</p>}
                </div>
              );
            })}
          </div>
        )}
        {task.category === 'transport' && task.related_guest && (
          <div className="border-t border-border pt-2 text-xs">
            <p className="font-semibold uppercase tracking-wide text-muted-foreground">Transport Context</p>
            <p className="mt-1 text-muted-foreground">Guest: <span className="font-medium text-foreground">{task.related_guest.name}</span></p>
            {task.related_guest.phone && <p className="text-muted-foreground">Guest Phone: {task.related_guest.phone}</p>}
            {task.related_guest.journey_status && (
              <p className="text-muted-foreground">Journey: {journeyStatusLabels[task.related_guest.journey_status] || task.related_guest.journey_status}</p>
            )}
            {task.related_guest.eta_to_hotel && (
              <p className="text-muted-foreground">ETA: {format(new Date(task.related_guest.eta_to_hotel), 'MMM d, h:mm a')}</p>
            )}
            {task.related_guest.arrival_details && <p className="text-muted-foreground">Travel: {task.related_guest.arrival_details}</p>}
          </div>
        )}
      </div>
    );
  };

  const TaskColumn = ({ status, title }) => {
    const columnTasks = getTasksByStatus(status);
    const groupedCategories = groupTasksForColumn(columnTasks);
    const config = statusConfig[status];

    return (
      <div className="flex-1 min-w-[280px]">
        <div className="flex items-center gap-2 mb-4">
          <config.icon className="w-5 h-5" />
          <h3 className="font-semibold">{title}</h3>
          <Badge variant="outline">{columnTasks.length}</Badge>
        </div>
        <div className="space-y-3">
          {groupedCategories.map((category) => (
            <div key={category.value} className="overflow-hidden rounded-md border border-border bg-card">
              {(() => {
                const CategoryIcon = category.icon || ClipboardText;
                const categoryKey = `${status}:${category.value}`;
                const isCollapsed = collapsedCategories[categoryKey] ?? true;
                const counts = getCategoryCounts(category.tasks);
                const urgencyIndicator = getCategoryUrgencyIndicator(category.urgency);
                return (
                  <>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 bg-muted/60 px-3 py-2 text-left hover:bg-muted"
                      onClick={() => toggleCategoryCollapsed(status, category.value)}
                    >
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        {isCollapsed ? (
                          <CaretRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        ) : (
                          <CaretDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <CategoryIcon className="h-4 w-4 shrink-0 text-wedding-sage" />
                        <span className="truncate text-sm font-semibold">{category.label}</span>
                        <Badge variant="outline" className="text-[0.6875rem]">{counts.pending} pending</Badge>
                      </span>
                      <span className="flex shrink-0 items-center gap-3">
                        {urgencyIndicator && (
                          <span className={`inline-flex items-center gap-1 text-[0.6875rem] font-medium ${urgencyIndicator.className}`}>
                            <span className="h-1.5 w-1.5 rounded-full bg-current" />
                            {urgencyIndicator.label}
                          </span>
                        )}
                        <span className="hidden text-[0.6875rem] text-muted-foreground sm:inline">
                          {counts.completed} done
                        </span>
                      </span>
                    </button>
                    {!isCollapsed && (
                      <div className="space-y-2 p-2">
                        {category.dueGroups.map((dueGroup) => (
                          <div key={dueGroup.key} className="space-y-1.5">
                            <div className="flex items-center gap-2 px-1">
                              <span className="text-[0.6875rem] font-medium uppercase tracking-wide text-muted-foreground">{dueGroup.label}</span>
                              <span className="h-px flex-1 bg-border" />
                            </div>
                            {dueGroup.tasks.map((task) => {
                              const isExpanded = expandedTaskIds.includes(task.id);
                              const transportAction = getTransportNextAction(task);
                              const TransportActionIcon = transportAction?.icon;
                              return (
                                <Card
                                  key={task.id}
                                  className={`cursor-pointer shadow-sm transition-shadow hover:shadow-md ${isExpanded ? 'ring-1 ring-wedding-sage/30' : ''}`}
                                  data-testid={`task-${task.id}`}
                                  onClick={() => toggleTaskExpanded(task.id)}
                                >
                                  <CardContent className="p-3">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-start gap-2">
                                          {isExpanded ? (
                                            <CaretDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                          ) : (
                                            <CaretRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                          )}
                                          <div className="min-w-0">
                                            <h4 className="truncate text-sm font-medium">{task.title}</h4>
                                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                                              <span>{formatDueSummary(task.due_date)}</span>
                                              {task.assigned_to && (
                                                <span className="inline-flex items-center gap-1" title={`Assigned to ${task.assigned_to}`}>
                                                  <Users className="h-3.5 w-3.5" /> {task.assigned_to}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex shrink-0 items-center gap-2" onClick={(event) => event.stopPropagation()}>
                                        <Badge className={`text-[0.6875rem] ${priorityBadgeClass(task.priority)}`}>
                                          {priorityLabels[task.priority] || `P${task.priority}`}
                                        </Badge>
                                        {!isTeamMember && (
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteTask(task.id)}
                                            className="text-muted-foreground hover:text-destructive transition-colors"
                                            data-testid={`delete-task-${task.id}`}
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>

                                    <div className="mt-2" onClick={(event) => event.stopPropagation()}>
                                      {task.category === 'transport' ? (
                                        transportAction ? (
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-7 w-full justify-start text-xs"
                                            onClick={() => handleJourneyAction(task, transportAction.value)}
                                          >
                                            <TransportActionIcon className="mr-2 h-4 w-4" />
                                            {transportAction.label}
                                          </Button>
                                        ) : (
                                          task.status !== 'completed' && (
                                            <p className="text-xs text-muted-foreground">
                                              Link a guest to run the transport workflow.
                                            </p>
                                          )
                                        )
                                      ) : (
                                        <Select
                                          value={task.status}
                                          onValueChange={(value) => handleUpdateStatus(task.id, value)}
                                        >
                                          <SelectTrigger className="h-7 text-xs" data-testid={`task-status-${task.id}`}>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="todo">To Do</SelectItem>
                                            <SelectItem value="in_progress">In Progress</SelectItem>
                                            <SelectItem value="completed">Completed</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      )}
                                    </div>

                                    {isExpanded && (
                                      <div className="mt-3 border-t border-border pt-3">
                                        {task.description && (
                                          <p className="text-sm text-muted-foreground mb-3">{task.description}</p>
                                        )}
                                        <div className="mb-3 flex flex-wrap gap-2">
                                          <Badge variant="outline">{categoryLabel(task.category)}</Badge>
                                          {task.related_guest_id && (
                                            <Badge className="bg-wedding-sage/10 text-wedding-sage">
                                              {getGuestName(task) || 'Linked Guest'}
                                            </Badge>
                                          )}
                                        </div>
                                        <VendorContext task={task} />
                                        {task.category !== 'transport' && journeyActions[task.category] && task.related_guest_id && (
                                          <div className="mb-3 grid gap-2" onClick={(event) => event.stopPropagation()}>
                                            {journeyActions[task.category].map((action) => (
                                              <Button
                                                key={action.value}
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="h-8 justify-start text-xs"
                                                onClick={() => handleJourneyAction(task, action.value)}
                                              >
                                                <action.icon className="mr-2 h-4 w-4" />
                                                {action.label}
                                              </Button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ))}
          {loading && (
            <Card className="border-dashed">
              <CardContent className="p-4 text-center text-sm text-muted-foreground">
                Loading tasks...
              </CardContent>
            </Card>
          )}
          {!loading && columnTasks.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-4 text-center text-sm text-muted-foreground">
                No tasks
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  const getMobilePrimaryAction = (task) => {
    if (task.category === 'transport') {
      const transportAction = getTransportNextAction(task);
      if (transportAction) {
        return transportAction;
      }
    }
    if (task.status === 'todo') {
      return { value: 'in_progress', label: 'Start', icon: Clock };
    }
    if (task.status === 'in_progress') {
      return { value: 'completed', label: 'Complete', icon: CheckCircle };
    }
    return { value: 'todo', label: 'Reopen', icon: Circle };
  };

  const renderMobileTaskCard = (task) => {
    const isExpanded = expandedTaskIds.includes(task.id);
    const action = getMobilePrimaryAction(task);
    const ActionIcon = action.icon;
    const isTransportWorkflow = task.category === 'transport' && getTransportNextAction(task);

    return (
      <div
        key={task.id}
        className={`rounded-md border bg-card shadow-sm transition-shadow ${isExpanded ? 'border-wedding-sage/50 ring-1 ring-wedding-sage/20' : 'border-border'}`}
        data-testid={`mobile-task-${task.id}`}
      >
        <button
          type="button"
          className="flex w-full items-start justify-between gap-3 px-3 py-3 text-left"
          onClick={() => toggleTaskExpanded(task.id)}
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-foreground">{task.title}</span>
            <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <span className="rounded-full bg-muted px-2 py-0.5">{formatDueSummary(task.due_date)}</span>
              {task.assigned_to && (
                <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted px-2 py-0.5">
                  <Users className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{task.assigned_to}</span>
                </span>
              )}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <Badge className={`text-[0.6875rem] ${priorityBadgeClass(task.priority)}`}>
              {priorityLabels[task.priority] || `P${task.priority}`}
            </Badge>
            {isExpanded ? (
              <CaretDown className="mt-0.5 h-4 w-4 text-muted-foreground" />
            ) : (
              <CaretRight className="mt-0.5 h-4 w-4 text-muted-foreground" />
            )}
          </span>
        </button>

        {isExpanded && (
          <div className="space-y-3 border-t border-border px-3 py-3">
            {task.description && (
              <p className="text-sm leading-5 text-muted-foreground">{task.description}</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{categoryLabel(task.category)}</Badge>
              {task.related_guest_id && (
                <Badge className="bg-wedding-sage/10 text-wedding-sage">
                  {getGuestName(task) || 'Linked Guest'}
                </Badge>
              )}
            </div>
            <VendorContext task={task} />
            {task.category !== 'transport' && journeyActions[task.category] && task.related_guest_id && (
              <div className="grid gap-2">
                {journeyActions[task.category].map((journeyAction) => (
                  <Button
                    key={journeyAction.value}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 justify-start text-xs"
                    onClick={() => handleJourneyAction(task, journeyAction.value)}
                  >
                    <journeyAction.icon className="mr-2 h-4 w-4" />
                    {journeyAction.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-3 py-2">
          <span className="text-xs font-medium text-muted-foreground">
            {statusConfig[task.status]?.label || 'Task'}
          </span>
          <div className="flex items-center gap-2">
            {!isTeamMember && (
              <button
                type="button"
                onClick={() => handleDeleteTask(task.id)}
                className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-background hover:text-destructive"
                aria-label={`Delete ${task.title}`}
                data-testid={`mobile-delete-task-${task.id}`}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <Button
              type="button"
              size="sm"
              variant={task.status === 'completed' ? 'outline' : 'default'}
              className={task.status === 'completed' ? 'h-8 text-xs' : 'h-8 bg-wedding-sage text-xs hover:bg-wedding-sage/90'}
              onClick={() => (
                isTransportWorkflow
                  ? handleJourneyAction(task, action.value)
                  : handleUpdateStatus(task.id, action.value)
              )}
            >
              <ActionIcon className="mr-1.5 h-4 w-4" />
              {action.label}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const MobileTaskBoard = () => {
    const mobileTasks = getTasksByStatus(mobileStatus);
    const groupedCategories = groupTasksForColumn(mobileTasks);
    const ActiveStatusIcon = statusConfig[mobileStatus].icon;

    return (
      <div className="space-y-3 md:hidden">
        <div className="rounded-md border border-border bg-card p-1 shadow-sm">
          <div className="grid grid-cols-3 gap-1">
            {taskStatusOrder.map((status) => {
              const StatusIcon = statusConfig[status].icon;
              const isActive = mobileStatus === status;
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => setMobileStatus(status)}
                  className={`flex min-w-0 flex-col items-center justify-center rounded px-2 py-2 text-xs transition-colors ${
                    isActive
                      ? 'bg-wedding-sage text-white shadow-sm'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <StatusIcon className="mb-1 h-4 w-4" />
                  <span className="max-w-full truncate font-medium">{statusConfig[status].label}</span>
                  <span className={isActive ? 'text-white/80' : 'text-muted-foreground'}>
                    {getTasksByStatus(status).length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2">
          <span className="inline-flex min-w-0 items-center gap-2 text-sm font-semibold">
            <ActiveStatusIcon className="h-4 w-4 shrink-0 text-wedding-sage" />
            <span className="truncate">{statusConfig[mobileStatus].label}</span>
          </span>
          <span className="text-xs text-muted-foreground">
            {mobileTasks.length} {mobileTasks.length === 1 ? 'task' : 'tasks'}
          </span>
        </div>

        <div className="space-y-3">
          {groupedCategories.map((category) => {
            const CategoryIcon = category.icon || ClipboardText;
            const categoryKey = `mobile:${mobileStatus}:${category.value}`;
            const isCollapsed = collapsedCategories[categoryKey] ?? false;
            const urgencyIndicator = getCategoryUrgencyIndicator(category.urgency);
            return (
              <section key={category.value} className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 bg-muted/50 px-3 py-2.5 text-left"
                  onClick={() => toggleCategoryCollapsed(`mobile:${mobileStatus}`, category.value)}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {isCollapsed ? (
                      <CaretRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <CaretDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <CategoryIcon className="h-4 w-4 shrink-0 text-wedding-sage" />
                    <span className="truncate text-sm font-semibold">{category.label}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    {urgencyIndicator && (
                      <span className={`inline-flex items-center gap-1 text-[0.6875rem] font-medium ${urgencyIndicator.className}`}>
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {urgencyIndicator.label}
                      </span>
                    )}
                    <Badge variant="outline" className="text-[0.6875rem]">
                      {category.tasks.length}
                    </Badge>
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="space-y-2 p-2">
                    {category.dueGroups.map((dueGroup) => (
                      <div key={dueGroup.key} className="space-y-2">
                        <div className="flex items-center gap-2 px-1">
                          <span className="text-[0.6875rem] font-medium uppercase tracking-wide text-muted-foreground">{dueGroup.label}</span>
                          <span className="h-px flex-1 bg-border" />
                        </div>
                        {dueGroup.tasks.map(renderMobileTaskCard)}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}

          {loading && (
            <Card className="border-dashed">
              <CardContent className="p-4 text-center text-sm text-muted-foreground">
                Loading tasks...
              </CardContent>
            </Card>
          )}
          {!loading && mobileTasks.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-4 text-center text-sm text-muted-foreground">
                No tasks here
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <StickyActionHeader>
          <div className="flex w-full items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="tasks-title">
                {isTeamMember ? 'My Tasks' : 'Task Board'}
              </h1>
              <p className="text-muted-foreground">
                {isTeamMember ? `${tasks.length} assigned to you` : `${tasks.length} tasks total`}
              </p>
            </div>
            {!isTeamMember && (
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button
                    size="icon"
                    className="shrink-0 bg-wedding-sage hover:bg-wedding-sage/90"
                    aria-label="Add task"
                    title="Add task"
                    data-header-action
                    data-testid="add-task-btn"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Task</DialogTitle>
                  <DialogDescription>Create a task for your event planning</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddTask} className="space-y-3">
                  <div>
                    <Label htmlFor="title">Task Title *</Label>
                    <Input
                      id="title"
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                      required
                      data-testid="task-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={newTask.description}
                      onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                      rows={3}
                      data-testid="task-description-input"
                    />
                  </div>
                  {activeTeamMembers.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="assigned_to_user_id">Assign To</Label>
                      <Select
                        value={newTask.assigned_to_user_id || 'unassigned'}
                        onValueChange={(value) => setNewTask({ ...newTask, assigned_to_user_id: value === 'unassigned' ? '' : value })}
                      >
                        <SelectTrigger id="assigned_to_user_id">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {activeTeamMembers.map((member) => (
                            <SelectItem key={member.user_id} value={member.user_id}>
                              {member.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="task_category">Category</Label>
                      <Select
                        value={newTask.category}
                        onValueChange={(value) => setNewTask({
                          ...newTask,
                          category: value,
                          related_guest_id: ['transport', 'hotel'].includes(value) ? newTask.related_guest_id : '',
                          journey_action: ['transport', 'hotel'].includes(value) ? newTask.journey_action : ''
                        })}
                      >
                        <SelectTrigger id="task_category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {taskCategories.map((category) => (
                            <SelectItem key={category.value} value={category.value}>
                              {category.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {['transport', 'hotel'].includes(newTask.category) && (
                      <div className="space-y-2">
                        <Label htmlFor="related_guest_id">Related Guest</Label>
                        <Select
                          value={newTask.related_guest_id || 'none'}
                          onValueChange={(value) => setNewTask({ ...newTask, related_guest_id: value === 'none' ? '' : value })}
                        >
                          <SelectTrigger id="related_guest_id">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No guest linked</SelectItem>
                            {guests.map((guest) => (
                              <SelectItem key={guest.id} value={guest.id}>
                                {guest.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  {journeyActions[newTask.category] && (
                    <div className="space-y-2">
                      <Label htmlFor="journey_action">Journey Action on Completion</Label>
                      <Select
                        value={newTask.journey_action || 'none'}
                        onValueChange={(value) => setNewTask({ ...newTask, journey_action: value === 'none' ? '' : value })}
                      >
                        <SelectTrigger id="journey_action">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Use category default</SelectItem>
                          {journeyActions[newTask.category].map((action) => (
                            <SelectItem key={action.value} value={action.value}>
                              {action.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <EventAwareDatePicker
                        id="due_date"
                        label="Due Date"
                        value={newTask.due_date}
                        onChange={(date) => setNewTask({ ...newTask, due_date: date })}
                        eventStartDate={currentEvent?.start_date}
                        eventEndDate={currentEvent?.end_date}
                        data-testid="task-due-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select
                        value={String(newTask.priority)}
                        onValueChange={(value) => setNewTask({ ...newTask, priority: parseInt(value) })}
                      >
                        <SelectTrigger data-testid="task-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">P1 - Urgent</SelectItem>
                          <SelectItem value="2">P2 - High</SelectItem>
                          <SelectItem value="3">P3 - Medium</SelectItem>
                          <SelectItem value="4">P4 - Low</SelectItem>
                          <SelectItem value="5">P5 - Nice to have</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90" data-testid="save-task-btn">
                      Add Task
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </StickyActionHeader>

        {/* Task Board */}
        <MobileTaskBoard />
        <div className="hidden gap-6 overflow-x-auto pb-4 md:flex">
          <TaskColumn status="todo" title="To Do" />
          <TaskColumn status="in_progress" title="In Progress" />
          <TaskColumn status="completed" title="Completed" />
        </div>
      </div>
    </DashboardLayout>
  );
}
